import { FC } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";

import { useModalConfirm } from "@ryde/redux";
import {
  capitalize,
  ConfirmResult,
  mergeTodayWithHHmmTime,
  TTimeHHmm,
  useFormats,
  useTimezone,
} from "@ryde/shared";

import { ColorIconButton } from "../../../controls";
import { CsvCellTemplate } from "../../templates-table/csv-list.template";
import {
  catchARideAvailabilityListConditions,
  ICatchARideAvailabilityItemForm,
} from "./catch-a-ride-availability.models";

export const CatchARideAvailabilityItemDisplay: FC<
  ICatchARideAvailabilityItemForm
> = ({ removeHandler, index, onEdit, value, disabledEdit, disabledRemove }) => {
  const { t } = useTranslation();
  const { timezone } = useTimezone();
  const { confirmDialogResult } = useModalConfirm();
  const { formatTimeNormalized } = useFormats();

  const onArchiveClickEvent = useEvent(async (): Promise<void> => {
    const confirmationResult = await confirmDialogResult({
      title: t("general.CONFIRM"),
      message: t("general.ITEM_ARCHIVE_MESSAGE"),
    });

    if (confirmationResult === ConfirmResult.OK) {
      removeHandler?.(index || 0);
    }
  });

  return (
    <div className="availability-item display-mode">
      <div className="availability-item__data">
        {catchARideAvailabilityListConditions.map(condition => {
          const list = value?.[condition.name];
          if (!list?.length) {
            return null;
          }
          const label = condition.isLabelCapitalized
            ? capitalize(t(condition.labelKey))
            : t(condition.labelKey);

          return (
            <div className="availability-item__data--row" key={condition.name}>
              <span className="availability-item__label">{label}:</span>
              {condition.isPlainTextValue ? (
                <div className="availability-cell">{list.join(", ")}</div>
              ) : (
                <CsvCellTemplate
                  name={condition.name}
                  customClassName="availability-cell"
                  value={list}
                  formAttributes={{
                    dropItemsSource: condition.dropItemsSource,
                  }}
                />
              )}
            </div>
          );
        })}
        {Boolean(value?.from) && (
          <div className="availability-item__data--row">
            <span className="availability-item__label">
              {t("general.FROM")}:
            </span>
            <div className="availability-cell">
              {formatTimeNormalized(
                mergeTodayWithHHmmTime(value?.from as TTimeHHmm, timezone),
              )}
            </div>
          </div>
        )}
        {Boolean(value?.to) && (
          <div className="availability-item__data--row">
            <span className="availability-item__label">{t("general.TO")}:</span>
            <div className="availability-cell">
              {formatTimeNormalized(
                mergeTodayWithHHmmTime(value?.to as TTimeHHmm, timezone),
              )}
            </div>
          </div>
        )}
      </div>
      <div className="availability-item__buttons">
        {!disabledEdit && (
          <ColorIconButton
            iconType="EDIT"
            edge="end"
            aria-label={t("general.EDIT")}
            className="edit-button"
            onClick={() => onEdit?.()}
          />
        )}
        {!disabledRemove && (
          <ColorIconButton
            iconType="DELETE"
            edge="end"
            aria-label={t("general.ARCHIVE")}
            onClick={() => onArchiveClickEvent()}
          />
        )}
      </div>
    </div>
  );
};
